// src/stores/auth.ts
import { defineStore } from 'pinia'
import api, { setAuthToken } from '../api/client'
import { AUTH_STORAGE_KEY, LEGACY_AUTH_STORAGE_KEYS } from '../constants/authStorage'
import { ROLE_GROUPS } from '../constants/roleGroups'
import { ADMIN_AUTH_APPLICATION_CODE } from '../services/adminAuth'
import { toApiRequestError } from '../services/apiErrors'

export interface CoreUser {
  id: number
  full_name: string
  primary_email: string
  status: string
  is_super_admin?: boolean
}

export interface CoreTenant {
  id: number
  code: string
  name: string
}

export interface CoreApplication {
  code: string
  name: string
}

export interface CoreRole {
  role_code: string
  role_name: string
  tenant_code: string | null
  app_code: string | null
}

interface AuthState {
  token: string | null
  user: CoreUser | null
  tenant: CoreTenant | null
  application: CoreApplication | null
  roles: CoreRole[]
  permissionCodes: string[]
  loading: boolean
  error: string | null
}

interface LoginPayload {
  email: string
  password: string
  application_code: string
  tenant_code?: string
}

interface AdminOtpLoginPayload {
  email: string
  otp_code: string
  application_code?: string
  tenant_code?: string
}

// âœ… helper: normalize backend roles into CoreRole[]
function normalizeRoles(rawRoles: any): CoreRole[] {
  if (!Array.isArray(rawRoles)) return []

  return rawRoles.map((r: any) => ({
    // backend uses "code" / "name" / "application" / "tenant"
    role_code: r?.role_code ?? r?.code ?? '',
    role_name: r?.role_name ?? r?.name ?? '',
    tenant_code: r?.tenant_code ?? r?.tenant ?? null,
    app_code: r?.app_code ?? r?.application ?? null,
  })).filter((r: CoreRole) => !!r.role_code)
}

function normalizePermissionCodes(rawCodes: unknown): string[] {
  if (!Array.isArray(rawCodes)) return []
  return Array.from(
    new Set(
      rawCodes
        .map((code) => String(code || '').trim().toLowerCase())
        .filter((code) => !!code),
    ),
  )
}

const ADMIN_ROLE_CODES = ROLE_GROUPS.ADMIN_PANEL
const ADMIN_SUPER_ROLE_CODES = ROLE_GROUPS.SUPER_ADMIN

function hasAdminAppRole(roles: CoreRole[]) {
  return roles.some((role) => {
    const appCode = String(role.app_code || '').toLowerCase()
    const roleCode = String(role.role_code || '').toLowerCase()
    return (
      appCode === ADMIN_AUTH_APPLICATION_CODE ||
      roleCode.startsWith('admin_') ||
      roleCode === 'administrator' ||
      roleCode === 'admin'
    )
  })
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    user: null,
    tenant: null,
    application: null,
    roles: [],
    permissionCodes: [],
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,

    hasAdminAppRole: (state) =>
      !!state.user?.is_super_admin || hasAdminAppRole(state.roles),

    hasAdminSuperRole: (state) =>
      !!state.user?.is_super_admin ||
      state.roles.some((role) => ADMIN_SUPER_ROLE_CODES.includes(role.role_code)),

    isAdmin: (state) =>
      !!state.user?.is_super_admin ||
      state.roles.some((r) => ADMIN_ROLE_CODES.includes(r.role_code)) ||
      hasAdminAppRole(state.roles),

    // Delete actions are limited to admins and super admins (not examiners/support).
    canDelete: (state) =>
      !!state.user?.is_super_admin ||
      state.roles.some((r) =>
        (ROLE_GROUPS.ADMINS as readonly string[]).includes(String(r.role_code || '').trim().toLowerCase()),
      ),

    hasRole: (state) => {
      return (code: string) => {
        const target = String(code || '').trim().toLowerCase()
        return state.roles.some(
          (r) => String(r.role_code || '').trim().toLowerCase() === target,
        )
      }
    },

    hasPermission: (state) => {
      return (code: string) => {
        if (state.user?.is_super_admin) return true
        const target = String(code || '').trim().toLowerCase()
        return state.permissionCodes.includes(target)
      }
    },

    hasAnyPermission: (state) => {
      return (codes: string[]) => {
        if (state.user?.is_super_admin) return true
        if (!Array.isArray(codes) || !codes.length) return true
        return codes.some((code) => {
          const target = String(code || '').trim().toLowerCase()
          return state.permissionCodes.includes(target)
        })
      }
    },
  },

  actions: {
    loadFromStorage() {
      try {
        let raw = localStorage.getItem(AUTH_STORAGE_KEY)

        if (!raw) {
          for (const legacyKey of LEGACY_AUTH_STORAGE_KEYS) {
            const legacyRaw = localStorage.getItem(legacyKey)
            if (legacyRaw) {
              raw = legacyRaw
              break
            }
          }
        }

        if (!raw) {
          setAuthToken(null)
          return
        }

        const parsed = JSON.parse(raw)
        this.token = parsed.token || null
        this.user = parsed.user || null
        this.tenant = parsed.tenant || null
        this.application = parsed.application || null
        this.roles = Array.isArray(parsed.roles) ? parsed.roles : []
        this.permissionCodes = normalizePermissionCodes(
          parsed.permission_codes || parsed.permissionCodes,
        )

        setAuthToken(this.token)
        this.persist()

        for (const legacyKey of LEGACY_AUTH_STORAGE_KEYS) {
          localStorage.removeItem(legacyKey)
        }
      } catch (e) {
        console.error('Failed to load auth from storage', e)
        this.logout()
      }
    },

    /**
     * Save current auth state to localStorage
     */
    persist() {
      const payload = {
        token: this.token,
        user: this.user,
        tenant: this.tenant,
        application: this.application,
        roles: this.roles,
        permission_codes: this.permissionCodes,
      }
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload))
    },

    /**
     * Core login using /api/core/login/
     */
    async login(payload: LoginPayload) {
      this.loading = true
      this.error = null

      try {
        const response = await api.post('/api/core/login/', {
          email: payload.email,
          password: payload.password,
          application_code: payload.application_code,
          tenant_code: payload.tenant_code,
        })

        const data = response.data

        this.token = data.token
        this.user = data.user
        this.tenant = data.tenant
        this.application = data.application
        this.roles = normalizeRoles(data.roles)
        this.permissionCodes = normalizePermissionCodes(data.permission_codes)

        setAuthToken(this.token)
        this.persist()
      } catch (err: any) {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.non_field_errors?.[0] ||
          'Login failed. Please check your credentials.'
        this.error = msg
        throw err
      } finally {
        this.loading = false
      }
    },

    /**
     * Admin OTP login using /api/core/admin/login/verify-otp/
     * Called after /admin/login/start/ has sent the OTP.
     */
    async loginAdminWithOtp(payload: AdminOtpLoginPayload) {
      this.loading = true
      this.error = null

      try {
        const response = await api.post('/api/core/admin/login/verify-otp/', {
          email: payload.email,
          otp_code: payload.otp_code,
          application_code: payload.application_code || ADMIN_AUTH_APPLICATION_CODE,
          tenant_code: payload.tenant_code,
        })

        const data = response.data

        // âœ… token key matches backend ("token")
        this.token = data.token
        this.user = data.user
        this.tenant = data.tenant
        this.application = data.application

        // âœ… normalize role objects
        this.roles = normalizeRoles(data.roles)
        this.permissionCodes = normalizePermissionCodes(data.permission_codes)

        setAuthToken(this.token)
        this.persist()
      } catch (err: any) {
        const normalized = toApiRequestError(err, 'OTP verification failed. Please try again.')
        this.error = normalized.isDomainRestriction
          ? 'Access is restricted to approved LWEGATECH staff domains.'
          : normalized.message
        throw normalized
      } finally {
        this.loading = false
      }
    },

    // Refresh user/tenant/roles from token on app boot.
    async fetchMe() {
      if (!this.token) return

      this.loading = true
      this.error = null

      try {
        const response = await api
          .get('/api/core/me/')
          .catch(() => api.get('/api/learn/me/'))
        const data = response.data

        this.user = data.user
        this.tenant = data.tenant
        this.application = data.application
        this.roles = normalizeRoles(data.roles)
        this.permissionCodes = normalizePermissionCodes(data.permission_codes)

        this.persist()
      } catch (err: any) {
        this.error = 'Session expired. Please log in again.'
        this.logout()
      } finally {
        this.loading = false
      }
    },

    logout() {
      this.clearAuthState()
    },

    clearAuthState() {
      this.token = null
      this.user = null
      this.tenant = null
      this.application = null
      this.roles = []
      this.permissionCodes = []
      this.loading = false
      this.error = null

      setAuthToken(null)
      localStorage.removeItem(AUTH_STORAGE_KEY)
      for (const legacyKey of LEGACY_AUTH_STORAGE_KEYS) {
        localStorage.removeItem(legacyKey)
      }
      sessionStorage.clear()
    },
  },
})
